import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/index";
import instance from "api/apiClient";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import Tooltip from "components/Tooltip";
import TextEditor from "components/wysiwyg/Wysiwyg";

interface Props {
  id: string;
  handleData: (e: any) => void;
  currentData: any;
  onInitialData?: (data: any) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

const PagesSeoSeoTab: React.FC<Props> = ({
  id,
  handleData,
  currentData,
  onInitialData,
  fieldErrors,
  clearFieldError,
  readOnly,
  onLoadingChange,
}) => {
  const { t } = useTranslation();

  const localesList = useAppSelector((store) => store.settings.locales);
  const geoList = useAppSelector((store) => store.settings.geoList);

  const [geo, setGeo] = useState<string>("");
  const [locale, setLocale] = useState<string>("");

  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [prevData, setPrevData] = useState<any>();

  useEffect(() => {
    onLoadingChange?.(!isFetched);
  }, [isFetched, onLoadingChange]);

  const handleGet = async () => {
    if (!id || !geo || !locale) {
      setIsFetched(true);
      return;
    }

    setIsFetched(false);

    try {
      const result = await instance.get(
        `/cms/pages/seo/attributes/${id}?geo=${geo}&locale=${locale}`,
      );

      handleData(result.data);
      setPrevData(result.data);
      onInitialData?.(result.data);
    } catch (error: any) {
      const code = error?.data?.error?.code;

      if (code === "BusinessError") {
        handleData({ geo, locale });
        setPrevData(undefined);
        onInitialData?.({ geo, locale });
      }
    }

    setIsFetched(true);
  };

  useEffect(() => {
    if (!geo || !locale) return;

    handleGet();
  }, [geo, locale, id]);

  useEffect(() => {
    if (!geo && geoList?.length) {
      setGeo(geoList[0]);
    }

    if (!locale && localesList?.length) {
      setLocale(localesList[0]);
    }
  }, [geoList, localesList]);

  if (!id) {
    return <div style={{ padding: 16 }}>Save page first</div>;
  }

  return (
    <div className="seo-tab-layout">
      <div className="cms-seo-tab mt-14">
        <div className="game-modal-tabs small">
          {geoList?.map((g) => (
            <div
              key={g}
              className={`tab-item ${geo === g ? "active" : ""}`}
              onClick={() => {
                setGeo(g);
                handleData({
                  ...(currentData || {}),
                  geo: g,
                });
              }}
            >
              {g.toLowerCase() === "ww" ? (
                <span className="mr-5">🌎</span>
              ) : (
                <img
                  src={`https://flagpedia.net/data/flags/icon/16x12/${g.toLowerCase()}.png`}
                  alt=""
                />
              )}

              {g}
            </div>
          ))}
        </div>
        <div className="game-modal-tabs small mt-14 mb-14">
          {localesList?.map((l) => (
            <div
              key={l}
              className={`tab-item ${locale === l ? "active" : ""}`}
              onClick={() => {
                setLocale(l);
                handleData({
                  ...(currentData || {}),
                  locale: l,
                });
              }}
            >
              <img
                src={`https://flagpedia.net/data/flags/icon/16x12/${l
                  .split("-")[1]
                  ?.toLowerCase()}.png`}
                alt=""
              />
              {t(l).replace(/-[a-z]+$/i, "")}
            </div>
          ))}
        </div>
      </div>

      <div className="seo-tab-scroll-content">
        {isFetched ? (
          <>
            <div className="game-form-content">
              <div className="game-form-box">
                <div className="game-form-box--item">
                  <InnerInput
                    id="seo-headTitle"
                    value={currentData.headTitle}
                    label={t("headTitle")}
                    tooltipText={t("headTitleTooltip")}
                    tooltipPosition="top-left"
                    type="text"
                    onChange={(e) => {
                      handleData({ ...currentData, headTitle: e });
                      clearFieldError("headTitle");
                    }}
                    hasError={Boolean(fieldErrors?.["headTitle"])}
                    disabled={readOnly}
                  />
                </div>

                <div className="game-form-box--item with-ai">
                  <InnerInput
                    id="seo-twitterTitle"
                    value={currentData.twitterTitle}
                    label={t("twTitle")}
                    tooltipText={t("twTitleTooltip")}
                    tooltipPosition="top-left"
                    type="text"
                    onChange={(e) => {
                      handleData({
                        ...currentData,
                        twitterTitle: e,
                      });
                      clearFieldError("twitterTitle");
                    }}
                    hasError={Boolean(fieldErrors?.["twitterTitle"])}
                    disabled={readOnly}
                  />
                </div>

                <div className="game-form-box--item">
                  <InnerInput
                    id="seo-openGraphTitle"
                    value={currentData.openGraphTitle}
                    label={t("ogTitle")}
                    tooltipText={t("ogTitleTooltip")}
                    tooltipPosition="top-left"
                    type="text"
                    onChange={(e) => {
                      handleData({
                        ...currentData,
                        openGraphTitle: e,
                      });
                      clearFieldError("openGraphTitle");
                    }}
                    hasError={Boolean(fieldErrors?.["openGraphTitle"])}
                    disabled={readOnly}
                  />
                </div>
              </div>

              <div className="game-form-box">
                <div className="game-form-box--item">
                  <InnerInput
                    id="seo-metaDescription"
                    value={currentData.metaDescription}
                    label={t("metaDescription")}
                    tooltipText={t("metaDescriptionTooltip")}
                    tooltipPosition="top-left"
                    type="textarea"
                    rows={1}
                    onChange={(e) => {
                      handleData({ ...currentData, metaDescription: e });
                      clearFieldError("metaDescription");
                    }}
                    hasError={Boolean(fieldErrors?.["metaDescription"])}
                    disabled={readOnly}
                  />
                </div>

                <div className="game-form-box--item with-ai">
                  <InnerInput
                    id="seo-twitterDescription"
                    value={currentData.twitterDescription}
                    label={t("twitterDescription")}
                    tooltipText={t("twitterDescriptionTooltip")}
                    tooltipPosition="top-left"
                    type="textarea"
                    rows={1}
                    onChange={(e) => {
                      handleData({
                        ...currentData,
                        twitterDescription: e,
                      });
                      clearFieldError("twitterDescription");
                    }}
                    hasError={Boolean(fieldErrors?.["twitterDescription"])}
                    disabled={readOnly}
                  />
                </div>

                <div className="game-form-box--item">
                  <InnerInput
                    id="seo-openGraphDescription"
                    value={currentData.openGraphDescription}
                    label={t("ogDescription")}
                    tooltipText={t("ogDescriptionTooltip")}
                    tooltipPosition="top-left"
                    type="textarea"
                    rows={1}
                    onChange={(e) => {
                      handleData({
                        ...currentData,
                        openGraphDescription: e,
                      });
                      clearFieldError("openGraphDescription");
                    }}
                    hasError={Boolean(fieldErrors?.["openGraphDescription"])}
                    disabled={readOnly}
                  />
                </div>
              </div>
            </div>

            <div className="game-form-full-description">
              <div className="game-form-box--item html-editor">
                <div className="label">
                  {t("fullDescription")}
                  <Tooltip
                    text={t("fullDescriptionTooltip")}
                    position="top-left"
                  >
                    <button className="info-icon">ℹ</button>
                  </Tooltip>
                </div>

                <TextEditor
                  value={currentData.fullDescription || ""}
                  onChange={(value) => {
                    handleData({
                      ...currentData,
                      fullDescription: value,
                    });

                    clearFieldError("fullDescription");
                  }}
                  hasError={Boolean(fieldErrors?.["fullDescription"])}
                  disabled={readOnly}
                />
              </div>

              <div className="game-form-box--item html-editor">
                <div className="label">
                  {t("additionalDescription")}
                  <Tooltip
                    text={t("additionalDescriptionTooltip")}
                    position="top-left"
                  >
                    <button className="info-icon">ℹ</button>
                  </Tooltip>
                </div>

                <TextEditor
                  value={currentData.additionalDescription || ""}
                  onChange={(value) => {
                    handleData({
                      ...currentData,
                      additionalDescription: value,
                    });

                    clearFieldError("additionalDescription");
                  }}
                  hasError={Boolean(fieldErrors?.["additionalDescription"])}
                  disabled={readOnly}
                />
              </div>
            </div>
          </>
        ) : (
          <ModalTabSkeleton />
        )}
      </div>
    </div>
  );
};

export default PagesSeoSeoTab;
